import { API_ENDPOINTS, httpClientInstance } from "@/api"
import { createFetchGuard, isFresh } from "@/utils/fetch-guard"
import { create } from "zustand"

export interface CostSummary {
  mtd_cost: string
  last_month_same_period_cost: string
  forecasted_cost_current_month: string | null
  last_month_total_cost: string
  currency: string
}

export interface UsageRecord {
  id: number
  instance: number
  instance_name: string
  instance_id: string
  owner: number
  owner_email: string
  start_time: string
  end_time: string
  hours_used: string
  hourly_rate: string
  total_cost: string
  billing_month: number
  billing_year: number
  is_active: boolean
  calculated_at: string
  created_at: string
  updated_at: string
}

export interface CreditsInfo {
  total_credits: string
  remaining_balance: string | null
  remaining_balance_note: string | null
  currency: string
}

/** Mirrors aws_connect/serializers.py EC2TotalCurrentCostSerializer (GET /v1/aws-connect/instances/get-current-month-cost/). */
export interface CurrentMonthCost {
  period_start: string
  period_end: string
  /** Decimal string with 6 places, EC2 compute cost for the month so far. */
  cost_usd: string
  unit: string
}

export interface InstanceBreakdown {
  instance_id: string
  instance_name: string
  total_cost: number
  total_hours: number
}

export interface UsageMonthlyReport {
  month: number
  year: number
  total_cost: string
  total_hours: string
  instance_count: number
  breakdown_by_instance: InstanceBreakdown[]
}

interface CostUsageState {
  // Cost summary
  summary: CostSummary | null
  isSummaryLoading: boolean
  summaryError: string | null
  /** Epoch ms of the last successful fetch; a failed fetch leaves it unchanged. */
  summaryFetchedAt: number | null
  /** Joins a pending request and skips while fresh (5 min); `force` bypasses the freshness check. */
  fetchSummary: (options?: { force?: boolean }) => Promise<void>

  // Detailed usage records
  usageRecords: UsageRecord[]
  availableInstances: { id: number; name: string }[]
  isUsageLoading: boolean
  usageError: string | null
  fetchUsage: (params?: { instance?: number; month?: number; year?: number }) => Promise<void>

  // Single usage record
  selectedRecord: UsageRecord | null
  isRecordLoading: boolean
  recordError: string | null
  fetchUsageRecord: (id: number) => Promise<void>
  clearSelectedRecord: () => void

  // Monthly report
  monthlyReport: UsageMonthlyReport | null
  isMonthlyReportLoading: boolean
  monthlyReportError: string | null
  fetchMonthlyReport: (month: number, year: number) => Promise<void>

  // Credits
  credits: CreditsInfo | null
  isCreditsLoading: boolean
  creditsError: string | null
  creditsFetchedAt: number | null
  fetchCredits: (options?: { force?: boolean }) => Promise<void>

  // Current month cost
  currentMonthCost: CurrentMonthCost | null
  isCurrentMonthCostLoading: boolean
  currentMonthCostError: string | null
  /** Epoch ms of the last successful fetch; a failed fetch leaves it unchanged. */
  currentMonthCostFetchedAt: number | null
  /** Joins a pending request and skips while fresh (5 min); `force` bypasses the freshness check. */
  fetchCurrentMonthCost: (options?: { force?: boolean }) => Promise<void>

  /** Drops the cached cost summary and credits and ignores any request still pending (sign-out, credential change). */
  reset: () => void
}

// Cost Explorer requests are billed: share one pending request per endpoint and skip refetches while fresh.
const summaryGuard = createFetchGuard()
const creditsGuard = createFetchGuard()
const currentMonthCostGuard = createFetchGuard()

export const useCostUsageStore = create<CostUsageState>((set, get) => ({
  summary: null,
  isSummaryLoading: false,
  summaryError: null,
  summaryFetchedAt: null,

  fetchSummary: async ({ force = false } = {}) => {
    if (!force && isFresh(get().summaryFetchedAt)) return
    await summaryGuard.run(async (isCurrent) => {
      set({ isSummaryLoading: true, summaryError: null })
      try {
        const data = await httpClientInstance.get<CostSummary>(API_ENDPOINTS.awsConnect.costSummary)
        if (isCurrent()) set({ summary: data, isSummaryLoading: false, summaryFetchedAt: Date.now() })
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Failed to fetch cost summary"
        if (isCurrent()) set({ summaryError: message, isSummaryLoading: false })
      }
    })
  },

  usageRecords: [],
  availableInstances: [],
  isUsageLoading: false,
  usageError: null,

  fetchUsage: async (params) => {
    set({ isUsageLoading: true, usageError: null })
    try {
      const qs = new URLSearchParams()
      if (params?.instance !== undefined) qs.set("instance", String(params.instance))
      if (params?.month !== undefined) qs.set("month", String(params.month))
      if (params?.year !== undefined) qs.set("year", String(params.year))
      const query = qs.toString()
      const url = query ? `${API_ENDPOINTS.awsConnect.usage.list}?${query}` : API_ENDPOINTS.awsConnect.usage.list
      const data = await httpClientInstance.get<UsageRecord[]>(url)

      // On the initial unfiltered call, derive the instance option list
      const isUnfiltered = !params?.instance && !params?.month && !params?.year
      if (isUnfiltered) {
        const seen = new Set<number>()
        const instances = data
          .filter((r) => {
            if (seen.has(r.instance)) return false
            seen.add(r.instance)
            return true
          })
          .map((r) => ({ id: r.instance, name: r.instance_name }))
        set({ usageRecords: data, availableInstances: instances, isUsageLoading: false })
      } else {
        set({ usageRecords: data, isUsageLoading: false })
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to fetch usage records"
      set({ usageError: message, isUsageLoading: false })
    }
  },

  selectedRecord: null,
  isRecordLoading: false,
  recordError: null,

  fetchUsageRecord: async (id: number) => {
    set({ isRecordLoading: true, recordError: null })
    try {
      const data = await httpClientInstance.get<UsageRecord>(API_ENDPOINTS.awsConnect.usage.retrieve(id))
      set({ selectedRecord: data, isRecordLoading: false })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to fetch usage record"
      set({ recordError: message, isRecordLoading: false })
    }
  },

  clearSelectedRecord: () => set({ selectedRecord: null, recordError: null }),

  credits: null,
  isCreditsLoading: false,
  creditsError: null,
  creditsFetchedAt: null,

  fetchCredits: async ({ force = false } = {}) => {
    if (!force && isFresh(get().creditsFetchedAt)) return
    await creditsGuard.run(async (isCurrent) => {
      set({ isCreditsLoading: true, creditsError: null })
      try {
        const data = await httpClientInstance.get<CreditsInfo>(API_ENDPOINTS.awsConnect.credits)
        if (isCurrent()) set({ credits: data, isCreditsLoading: false, creditsFetchedAt: Date.now() })
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Failed to fetch credits"
        if (isCurrent()) set({ creditsError: message, isCreditsLoading: false })
      }
    })
  },

  currentMonthCost: null,
  isCurrentMonthCostLoading: false,
  currentMonthCostError: null,
  currentMonthCostFetchedAt: null,

  fetchCurrentMonthCost: async ({ force = false } = {}) => {
    if (!force && isFresh(get().currentMonthCostFetchedAt)) return
    await currentMonthCostGuard.run(async (isCurrent) => {
      set({ isCurrentMonthCostLoading: true, currentMonthCostError: null })
      try {
        const data = await httpClientInstance.get<CurrentMonthCost>(API_ENDPOINTS.awsConnect.currentMonthCost)
        if (isCurrent()) {
          set({ currentMonthCost: data, isCurrentMonthCostLoading: false, currentMonthCostFetchedAt: Date.now() })
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Failed to fetch current month cost"
        if (isCurrent()) set({ currentMonthCostError: message, isCurrentMonthCostLoading: false })
      }
    })
  },

  monthlyReport: null,
  isMonthlyReportLoading: false,
  monthlyReportError: null,

  fetchMonthlyReport: async (month: number, year: number) => {
    set({ isMonthlyReportLoading: true, monthlyReportError: null })
    try {
      const data = await httpClientInstance.get<UsageMonthlyReport>(
        `${API_ENDPOINTS.awsConnect.usage.monthlyReport}?month=${month}&year=${year}`
      )
      set({ monthlyReport: data, isMonthlyReportLoading: false })
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to fetch monthly report"
      set({ monthlyReportError: message, isMonthlyReportLoading: false })
    }
  },

  reset: () => {
    summaryGuard.reset()
    creditsGuard.reset()
    currentMonthCostGuard.reset()
    set({
      summary: null,
      isSummaryLoading: false,
      summaryError: null,
      summaryFetchedAt: null,
      credits: null,
      isCreditsLoading: false,
      creditsError: null,
      creditsFetchedAt: null,
      currentMonthCost: null,
      isCurrentMonthCostLoading: false,
      currentMonthCostError: null,
      currentMonthCostFetchedAt: null,
    })
  },
}))
